import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import PopAnnouncementEditModal from './PopAnnouncementEditModal';
import {
  EN_LANG_CODE,
  POP_STATE,
  formatPopState,
  hasPopDetailContent,
  mergePopDetailsWithLanguages,
} from './popAnnouncementConstants';

function DetailValue({ value, isHtml = false }) {
  if (!value?.trim()) {
    return <span className="pop-announcement-view__empty">未設定</span>;
  }
  if (isHtml) {
    return (
      <div
        className="pop-announcement-view__html"
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  return <span>{value}</span>;
}

export default function PopAnnouncementPage() {
  const [config, setConfig] = useState({ state: POP_STATE.OFF, details: [] });
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const mergedDetails = useMemo(
    () => mergePopDetailsWithLanguages(languages, config.details || []),
    [languages, config.details]
  );

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [langList, popData] = await Promise.all([
        adminApi.newsLanguages(),
        adminApi.getPop(),
      ]);
      setLanguages(Array.isArray(langList) ? langList : []);
      setConfig(popData || { state: POP_STATE.OFF, details: [] });
    } catch (err) {
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function handleSaved() {
    setMessage('修改成功');
    setEditOpen(false);
    loadData();
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>彈窗公告</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-success" onClick={() => setEditOpen(true)}>
            修改
          </button>
        </div>
      </div>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      {loading ? (
        <p className="table-empty">載入中…</p>
      ) : (
        <div className="pop-announcement-view">
          <div className="pop-announcement-view__row">
            <span className="pop-announcement-view__label">狀態</span>
            <span className={config.state === POP_STATE.ON ? 'status-text status-text--success' : 'status-text'}>
              {formatPopState(config.state)}
            </span>
          </div>

          <h3 className="pop-announcement-view__section-title">標題</h3>
          {mergedDetails.map((detail) => (
            <div key={`title-${detail.lang}`} className="pop-announcement-view__row">
              <span className="pop-announcement-view__label">標題（{detail._label || detail.lang}）</span>
              <DetailValue value={detail.title} />
            </div>
          ))}

          <h3 className="pop-announcement-view__section-title">內容</h3>
          {mergedDetails.map((detail) => (
            <div key={`body-${detail.lang}`} className="pop-announcement-view__row pop-announcement-view__row--stack">
              <span className="pop-announcement-view__label">內容（{detail._label || detail.lang}）</span>
              <DetailValue value={detail.body} isHtml />
            </div>
          ))}

          <h3 className="pop-announcement-view__section-title">圖片</h3>
          {mergedDetails.map((detail) => (
            <div key={`image-${detail.lang}`} className="pop-announcement-view__row pop-announcement-view__row--stack">
              <span className="pop-announcement-view__label">圖片（{detail._label || detail.lang}）</span>
              {detail.image ? (
                <img src={resolveAdminMediaUrl(detail.image)} alt="" className="pop-announcement-view__image" />
              ) : (
                <span className="pop-announcement-view__empty">未設定</span>
              )}
            </div>
          ))}

          <div className="pop-announcement-view__preview-hint">
            列表預覽語系：英語（{EN_LANG_CODE}）
            {hasPopDetailContent(mergedDetails.find((item) => item.lang === EN_LANG_CODE))
              ? ' — 已配置'
              : ' — 未設定'}
          </div>
        </div>
      )}

      <PopAnnouncementEditModal
        open={editOpen}
        initialData={config}
        languages={languages}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
